/**
 * Smoothing of the CAN graph series.
 *
 * The tank fuel level swings with the truck: braking, a ramp or a curve tilts the fuel against the
 * float and the reading drops for a couple of messages before coming back. Plotted raw, those swings
 * look like the same vertical step a real drain leaves behind, and the eye cannot tell them apart.
 * Averaging the reading over the last few messages flattens the swing — which lasts one or two
 * points — while leaving a genuine drop standing, because a genuine drop does not come back up.
 */

/** Readings, counting the current one, that form the fuel-level window. */
export const FUEL_LEVEL_SMOOTHING_WINDOW = 5;

/** A reading the tracker did not report. It is not a zero: it is the absence of a measurement. */
export type Reading = number | null | undefined;

/**
 * Moving average over the `windowSize` readings ENDING on each point.
 *
 * The window trails the point instead of straddling it, so the value drawn over an instant never
 * borrows from readings that had not arrived yet — a centred window would start bending a drop
 * before it happened.
 *
 * Two rules keep the average honest where the data is thin:
 * - the first points average the few readings that exist instead of being dropped, so the series
 *   starts where the data starts;
 * - a gap is skipped, never counted as a zero, and it does not reset the window either. A point only
 *   comes out empty when its whole window is empty, and it stays empty — an absent reading is not
 *   an empty tank.
 *
 * @param values Series in chronological order, gaps included
 * @param windowSize Number of readings the average looks back over, current point included
 * @returns Series of the same length, `null` wherever the window held no reading at all
 */
export const trailingMovingAverage = (values: Reading[], windowSize: number): (number | null)[] => {
  const span = Math.max(Math.trunc(windowSize), 1);

  return values.map((_reading, index) => {
    const window = values.slice(Math.max(0, index - span + 1), index + 1);
    const readings = window.filter((entry): entry is number => typeof entry === "number" && Number.isFinite(entry));

    if (readings.length === 0) return null;

    return readings.reduce((total, entry) => total + entry, 0) / readings.length;
  });
};
